import type { BotRuntime, BotRuntimeSnapshot } from "./bot-runtime";
import type { CombatRuntime } from "./combat-runtime";
import type { FoodRuntime } from "./food-runtime";
import type { GameFlow, GameFlowSnapshot } from "./game-flow";
import type { PlayerRuntime } from "./player-runtime";
import { gameplayConfig } from "../config/gameplay-config";
import {
  createBotDefeatPlan,
  type AcceptedBotDefeatPlan,
  type IgnoredBotDefeatReason,
} from "../simulation/bot-defeat";
import { SeededRandom } from "../simulation/seeded-random";
import type { DefeatCombatResolution } from "../simulation/size-combat";
import { recordBoundedIdentifier } from "./runtime-stability";

export interface BotDefeatRecord {
  readonly contactId: string;
  readonly defeatedId: string;
  readonly droppedFoodCount: number;
  readonly droppedFoodIds: readonly string[];
  readonly dropPosition: { readonly x: number; readonly y: number };
  readonly rewardCoins: number;
  readonly runId: number;
  readonly winnerId: string;
}

export interface BotDefeatRuntimeSnapshot {
  readonly lastDefeat: BotDefeatRecord | null;
  readonly pendingReward: number;
  readonly snakesDefeated: number;
}

export interface BotDefeatRuntimeStabilitySnapshot {
  readonly pendingRewardEventCount: number;
  readonly processedEventCount: number;
}

export interface IgnoredBotDefeatOutcome {
  readonly kind: "ignored";
  readonly reason: IgnoredBotDefeatReason | "food-drop-unavailable";
}

export interface AcceptedBotDefeatOutcome extends BotDefeatRecord {
  readonly kind: "accepted";
}

export type BotDefeatOutcome =
  AcceptedBotDefeatOutcome | IgnoredBotDefeatOutcome;
export type BotDefeatListener = (outcome: AcceptedBotDefeatOutcome) => void;

type BotPort = Pick<BotRuntime, "getSnapshot" | "remove">;
type CombatPort = Pick<CombatRuntime, "subscribe">;
type FoodPort = Pick<FoodRuntime, "commitDefeatDrop" | "planDefeatDrop">;
type PlayerPort = Pick<PlayerRuntime, "getSnapshot">;

const findBot = (
  snapshot: BotRuntimeSnapshot,
  identifier: string,
): BotRuntimeSnapshot["bots"][number] | null =>
  snapshot.bots.find((bot) => bot.id === identifier) ?? null;

const cloneRecord = (record: BotDefeatRecord): BotDefeatRecord => ({
  ...record,
  dropPosition: { ...record.dropPosition },
  droppedFoodIds: [...record.droppedFoodIds],
});

export class BotDefeatRuntime {
  private activeRunId = 0;
  private lastDefeat: BotDefeatRecord | null = null;
  private readonly listeners = new Set<BotDefeatListener>();
  private readonly pendingRewardEvents = new Map<string, number>();
  private pendingReward = 0;
  private readonly processedEventKeys = new Set<string>();
  private seed: string;
  private snakesDefeated = 0;

  public constructor(
    flow: GameFlow,
    combatRuntime: CombatPort,
    private readonly playerRuntime: PlayerPort,
    private readonly botRuntime: BotPort,
    private readonly foodRuntime: FoodPort,
    seed: string,
  ) {
    this.seed = seed;
    combatRuntime.subscribe((resolution) => {
      if (resolution.kind === "defeat") {
        this.consume(resolution);
      }
    });
    flow.subscribe((snapshot) => {
      this.applyFlow(snapshot);
    });
  }

  public consume(event: DefeatCombatResolution): BotDefeatOutcome {
    const playerSnapshot = this.playerRuntime.getSnapshot();
    const botSnapshot = this.botRuntime.getSnapshot();
    const defeatedBot = findBot(botSnapshot, event.defeatedId);
    const winnerExists =
      (event.winnerId === "player" && playerSnapshot.player !== null) ||
      findBot(botSnapshot, event.winnerId) !== null;
    const random = new SeededRandom(
      `${this.seed}:bot-defeat:${String(event.runId)}:${event.contactId}`,
    );
    const plan = createBotDefeatPlan(
      {
        activeRunId: this.activeRunId,
        currentSimulationTimeMs: playerSnapshot.simulationTimeMs,
        defeatedBot:
          defeatedBot === null
            ? null
            : { id: defeatedBot.id, position: defeatedBot.position },
        event,
        processedEventKeys: this.processedEventKeys,
        winnerExists,
      },
      gameplayConfig.combat,
      gameplayConfig.rewards,
      random,
    );
    if (plan.kind === "ignored") {
      return plan;
    }
    let availableDropCount = plan.dropCount;
    let foodPlan = this.foodRuntime.planDefeatDrop(
      plan.dropPosition,
      availableDropCount,
      random,
    );
    while (foodPlan === null && availableDropCount > 0) {
      availableDropCount -= 1;
      foodPlan = this.foodRuntime.planDefeatDrop(
        plan.dropPosition,
        availableDropCount,
        random,
      );
    }
    if (foodPlan === null) {
      return { kind: "ignored", reason: "food-drop-unavailable" };
    }
    const removed = this.botRuntime.remove(plan.defeatedId);
    if (removed === null) {
      return { kind: "ignored", reason: "unknown-bot" };
    }
    this.foodRuntime.commitDefeatDrop(foodPlan);
    return this.record(
      event,
      plan,
      foodPlan.foods.map((food) => food.id),
    );
  }

  public getSnapshot(): BotDefeatRuntimeSnapshot {
    return {
      lastDefeat:
        this.lastDefeat === null ? null : cloneRecord(this.lastDefeat),
      pendingReward: this.pendingReward,
      snakesDefeated: this.snakesDefeated,
    };
  }

  public getStabilitySnapshot(): BotDefeatRuntimeStabilitySnapshot {
    return {
      pendingRewardEventCount: this.pendingRewardEvents.size,
      processedEventCount: this.processedEventKeys.size,
    };
  }

  public acknowledgeReward(record: BotDefeatRecord): boolean {
    const eventKey = `${String(record.runId)}:${record.contactId}`;
    const reward = this.pendingRewardEvents.get(eventKey);
    if (reward === undefined || reward !== record.rewardCoins) {
      return false;
    }
    this.pendingRewardEvents.delete(eventKey);
    this.pendingReward -= reward;
    return true;
  }

  public setSeed(seed: string): void {
    this.seed = seed;
  }

  public subscribe(listener: BotDefeatListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private applyFlow(snapshot: GameFlowSnapshot): void {
    if (snapshot.scene === "results") {
      return;
    }
    this.activeRunId =
      snapshot.scene === "gameplay"
        ? this.playerRuntime.getSnapshot().runId
        : 0;
    this.lastDefeat = null;
    this.pendingRewardEvents.clear();
    this.pendingReward = 0;
    this.snakesDefeated = 0;
  }

  private record(
    event: DefeatCombatResolution,
    plan: AcceptedBotDefeatPlan,
    droppedFoodIds: readonly string[],
  ): AcceptedBotDefeatOutcome {
    recordBoundedIdentifier(
      this.processedEventKeys,
      plan.eventKey,
      gameplayConfig.runtimeStability.maximumProcessedEvents,
    );
    if (plan.rewardCoins > 0) {
      this.pendingRewardEvents.set(plan.eventKey, plan.rewardCoins);
      this.pendingReward += plan.rewardCoins;
    }
    this.snakesDefeated += plan.snakesDefeated;
    const record: BotDefeatRecord = {
      contactId: event.contactId,
      defeatedId: plan.defeatedId,
      droppedFoodCount: droppedFoodIds.length,
      droppedFoodIds: [...droppedFoodIds],
      dropPosition: { ...plan.dropPosition },
      rewardCoins: plan.rewardCoins,
      runId: plan.runId,
      winnerId: plan.winnerId,
    };
    this.lastDefeat = record;
    const outcome = { ...record, kind: "accepted" as const };
    for (const listener of this.listeners) {
      listener({
        ...outcome,
        dropPosition: { ...outcome.dropPosition },
        droppedFoodIds: [...outcome.droppedFoodIds],
      });
    }
    return outcome;
  }
}
